import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, ChevronRight, Trophy, Users, Calendar } from 'lucide-react';
import { api } from '@/api';

/**
 * Global cross-collection search.
 *
 * Two display modes (controlled by `variant`):
 *   - "inline"  – always-on input, used in desktop nav
 *   - "icon"    – starts as a magnifying-glass icon, expands into a fullscreen
 *                 overlay when clicked. Used for mobile.
 *
 * Behaviour identical in both: 300ms-debounced fetch from /api/search → live
 * dropdown with up-to-5 picks per category + "See all results →" link to /search?q=.
 */
export const GlobalSearch = ({ variant = 'inline' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(variant === 'inline');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce the query so we don't hammer the backend on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch when debounced query changes
  useEffect(() => {
    if (!debounced) { setResults(null); return; }
    let cancelled = false;
    setLoading(true);
    api.search(debounced, 5).then(res => {
      if (!cancelled) setResults(res.data);
    }).catch(err => {
      console.error('Search failed', err);
      if (!cancelled) setResults(null);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [debounced]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Esc to close the icon-variant overlay
  useEffect(() => {
    if (variant !== 'icon' || !open) return;
    const onKey = (e) => { if (e.key === 'Escape') closeOverlay(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variant]);

  // Auto-focus when icon-variant overlay opens
  useEffect(() => {
    if (variant === 'icon' && open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, variant]);

  const closeOverlay = useCallback(() => {
    setOpen(false);
    setQuery('');
    setResults(null);
    setShowDropdown(false);
  }, []);

  const goTo = useCallback((path) => {
    setShowDropdown(false);
    if (variant === 'icon') closeOverlay();
    navigate(path);
  }, [navigate, variant, closeOverlay]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    goTo(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const totalShown = (results?.teams?.length || 0) + (results?.leagues?.length || 0) + (results?.games?.length || 0);
  const totalAll = (results?.totals?.teams || 0) + (results?.totals?.leagues || 0) + (results?.totals?.games || 0);

  // ============= Icon-trigger mode (mobile) =============
  if (variant === 'icon' && !open) {
    return (
      <button
        className="md:hidden text-white p-2 hover:text-white/70 transition-colors"
        onClick={() => setOpen(true)}
        aria-label="Open search"
        data-testid="global-search-open-mobile"
      >
        <Search className="w-5 h-5" />
      </button>
    );
  }

  // ============= Icon-trigger mode (mobile, expanded as overlay) =============
  if (variant === 'icon' && open) {
    return (
      <div className="fixed inset-0 z-[100] bg-gray-950/95 backdrop-blur-md flex flex-col md:hidden" data-testid="global-search-overlay">
        <div className="flex items-center gap-2 p-3 border-b border-gray-800">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder="Search teams, conferences, games…"
            className="flex-1 bg-transparent text-white text-base outline-none placeholder:text-gray-500"
            data-testid="global-search-input"
          />
          <button onClick={closeOverlay} className="text-gray-400 hover:text-white" data-testid="global-search-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ResultsList
            query={debounced}
            results={results}
            loading={loading}
            totalShown={totalShown}
            totalAll={totalAll}
            onGoTo={goTo}
            mobile
          />
        </div>
      </div>
    );
  }

  // ============= Inline mode (desktop nav) =============
  return (
    <div ref={wrapperRef} className="relative hidden md:block">
      <form onSubmit={onSubmit} className="flex items-center bg-white/10 hover:bg-white/15 focus-within:bg-white/15 rounded-full px-3 py-1.5 transition-colors min-w-[260px]" data-testid="global-search-wrapper">
        <Search className="w-4 h-4 text-white/70 shrink-0 mr-2" />
        <input
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search teams, conferences, games…"
          className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/60 w-full"
          data-testid="global-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults(null); inputRef.current?.focus(); }}
            className="text-white/60 hover:text-white shrink-0"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </form>

      {showDropdown && debounced && (
        <div
          className="absolute top-full mt-2 right-0 w-[420px] max-w-[90vw] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-[60]"
          data-testid="global-search-dropdown"
        >
          <ResultsList
            query={debounced}
            results={results}
            loading={loading}
            totalShown={totalShown}
            totalAll={totalAll}
            onGoTo={goTo}
          />
        </div>
      )}
    </div>
  );
};

// ============= Shared results-list view =============
const ResultsList = ({ query, results, loading, totalShown, totalAll, onGoTo, mobile = false }) => {
  if (loading && !results) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Searching…
      </div>
    );
  }
  if (!results) return null;
  if (totalShown === 0) {
    return (
      <div className="py-8 text-center text-gray-400 text-sm" data-testid="global-search-empty">
        No results for <span className="text-white">"{query}"</span>
      </div>
    );
  }
  return (
    <div className={`max-h-[70vh] overflow-y-auto ${mobile ? 'p-2' : ''}`}>
      {results.teams.length > 0 && (
        <Section title="Teams" icon={Users}>
          {results.teams.map(t => (
            <ResultRow
              key={`t-${t.id}`}
              testId={`search-result-team-${t.id}`}
              title={t.name}
              subtitle={t.short_name && t.short_name !== t.name ? t.short_name : ''}
              image={t.logo}
              onClick={() => onGoTo(`/team/${t.slug}`)}
            />
          ))}
        </Section>
      )}
      {results.leagues.length > 0 && (
        <Section title="Conferences" icon={Trophy}>
          {results.leagues.map(l => (
            <ResultRow
              key={`l-${l.id}`}
              testId={`search-result-league-${l.id}`}
              title={l.name}
              subtitle={l.region ? `${l.region.toUpperCase()} region` : ''}
              image={l.logo}
              onClick={() => onGoTo(`/league/${l.slug}`)}
            />
          ))}
        </Section>
      )}
      {results.games.length > 0 && (
        <Section title="Games" icon={Calendar}>
          {results.games.map(g => (
            <ResultRow
              key={`g-${g.id}`}
              testId={`search-result-game-${g.id}`}
              title={`${g.home_team_name} vs ${g.away_team_name}`}
              subtitle={[
                g.league_name,
                g.scheduled_time ? new Date(g.scheduled_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '',
                g.venue,
              ].filter(Boolean).join(' • ')}
              image={g.thumbnail_url}
              onClick={() => onGoTo(`/watch/${g.id}`)}
            />
          ))}
        </Section>
      )}
      {totalAll > totalShown && (
        <button
          onClick={() => onGoTo(`/search?q=${encodeURIComponent(query)}`)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm text-sky-400 hover:text-sky-300 hover:bg-gray-800/50 border-t border-gray-800 transition-colors"
          data-testid="global-search-see-all"
        >
          See all {totalAll} results
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
      <Icon className="w-3 h-3" /> {title}
    </div>
    <div>{children}</div>
  </div>
);

const ResultRow = ({ title, subtitle, image, onClick, testId }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/70 transition-colors text-left"
    data-testid={testId}
  >
    <div className="w-9 h-9 bg-gray-800 rounded-md overflow-hidden flex items-center justify-center shrink-0">
      {image ? (
        <img src={image} alt="" className="w-full h-full object-cover" />
      ) : (
        <Search className="w-4 h-4 text-gray-600" />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-white text-sm font-medium truncate">{title}</div>
      {subtitle && <div className="text-xs text-gray-400 truncate">{subtitle}</div>}
    </div>
  </button>
);

export default GlobalSearch;
